import apiClient from './apiClient';
import { Booking, BookingForm } from '@/types';
import {
  ServiceCategory,
  VehicleServiceType,
  DriverServiceType,
  BookingDurationType,
  BookingApprovalType,
  PricingBreakdown
} from '@/types/index';
import { generateBookingReference, type BookingType } from '@/utils/bookingReferenceGenerator';
import { getApiBaseUrl } from '@/config/environment';
import { logger } from '@/utils/logger';
import { postWithAvailabilityOverride } from '@/utils/availabilityOverride';

const log = logger.module('BookingService');


// Mock yacht data for fallback
const getMockYachts = () => [
  { id: 'yacht1', name: 'Sea Breeze', model: 'Sunseeker 60', capacity: 12, location: 'Lagos Marina' },
  { id: 'yacht2', name: 'Ocean Dream', model: 'Azimut 55', capacity: 10, location: 'Victoria Island Marina' },
  { id: 'yacht3', name: 'Blue Horizon', model: 'Princess V50', capacity: 8, location: 'Lekki Marina' }
];

// const API_BASE = getApiBaseUrl(); // Old base API (commented out)
const API_BASE = getApiBaseUrl(); // Original base API

// Simple booking service for direct API access
export const simplBookingService = {
  // Get all bookings directly from API (enhanced endpoint preferred, fallback to legacy)
  async getAllBookings(options?: { page?: number; limit?: number; status?: string; search?: string }) {
    try {
      log.info('Fetching all bookings', { options });

      // Build query parameters
      const params = new URLSearchParams();
      if (options?.page) params.append('page', options.page.toString());
      // Default limit to 1000 to fetch all bookings (API default is only 10)
      params.append('limit', (options?.limit || 1000).toString());
      if (options?.status) params.append('status', options.status);
      if (options?.search) params.append('search', options.search);

      const queryString = params.toString();
      const url = `${getApiBaseUrl()}/api/admin/bookings${queryString ? `?${queryString}` : ''}`;

      log.debug('Making API request', { url, queryString });

      // Prepare headers with authentication
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };

      // Add authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const adminResponse = await fetch(url, {
        method: 'GET',
        headers
      });


      if (adminResponse.ok) {
        const responseText = await adminResponse.text();

        let response;
        try {
          response = JSON.parse(responseText);
        } catch (parseError) {
          log.error('JSON parse error', parseError);
          log.error('Response was not valid JSON', { preview: responseText.substring(0, 500) });
          return [];
        }


        // Handle multiple possible API response structures
        let bookingsData = [];

        if (response.success && response.data) {
          bookingsData = response.data;
        } else if (Array.isArray(response.data)) {
          bookingsData = response.data;
        } else if (Array.isArray(response)) {
          bookingsData = response;
        } else if (response.bookings && Array.isArray(response.bookings)) {
          bookingsData = response.bookings;
        } else {
          log.error('Unexpected response structure', { keys: Object.keys(response), response });
          return [];
        }

        if (bookingsData.length > 0) {
          log.info('Bookings fetched successfully', { count: bookingsData.length });
          // Transform API data to match frontend expectations
          const transformedBookings = bookingsData.map((apiBooking: any, index: number) => {

            // Normalize serviceCategory (API has inconsistent values)
            let normalizedCategory = apiBooking.serviceCategory;
            if (apiBooking.serviceCategory === 'self_drive' || apiBooking.serviceCategory === 'vehicle') {
              normalizedCategory = 'vehicle-rental';
            }

            // Check if this is a driver-rental booking (personal driver, uses client's car)
            const isDriverRental = normalizedCategory === 'driver-rental' ||
                                apiBooking.booking?.type === 'driver-rental' ||
                                apiBooking.vehicle?.type === 'Professional Driver';

            // For driver-rental bookings, derive serviceType from duration
            let derivedServiceType = apiBooking.serviceType || apiBooking.booking?.type || apiBooking.bookingType || 'rental';
            if (isDriverRental && apiBooking.booking?.duration) {
              const duration = apiBooking.booking.duration.toLowerCase();
              if (duration.includes('hour') && !duration.includes('day')) {
                const hours = parseInt(duration);
                if (hours <= 4) derivedServiceType = 'hourly';
                else if (hours <= 6) derivedServiceType = 'half-day';
                else derivedServiceType = 'daily';
              } else if (duration.includes('day')) {
                if (duration.includes('half')) derivedServiceType = 'half-day';
                else if (duration.includes('week') || parseInt(duration) >= 7) derivedServiceType = 'weekly';
                else derivedServiceType = 'daily';
              }
            }

            // Check if this is an airport transfer
            const isAirportTransfer = normalizedCategory === 'airport-transfer' ||
                                     apiBooking.serviceCategory === 'airport_transfer' ||
                                     apiBooking.serviceType === 'airport-transfer' ||
                                     apiBooking.bookingType === 'airport-transfer';

            // Get duration with special handling for airport transfers
            let bookingDuration = apiBooking.booking?.duration || apiBooking.duration;

            // For airport transfers: default to "2 hours" if duration is missing, negative, or invalid
            if (isAirportTransfer) {
              if (!bookingDuration ||
                  bookingDuration === 'N/A' ||
                  (typeof bookingDuration === 'number' && bookingDuration < 0) ||
                  (typeof bookingDuration === 'string' && bookingDuration.includes('-'))) {
                bookingDuration = '2 hours';
              }
            } else if (!bookingDuration) {
              bookingDuration = 'N/A';
            }

            // Extract corporate account data from nested structure
            const isCorporate = apiBooking.isCorporate || false;
            const corporateAccount = apiBooking.corporateAccount || null;
            const displayName = isCorporate && corporateAccount?.companyName
              ? corporateAccount.companyName
              : (apiBooking.customer?.name || apiBooking.customerName || 'Unknown Customer');

            const transformed = {
              // CRITICAL: Use _id (database UUID) for all operations, NOT bookingReference
              id: apiBooking._id || apiBooking.id, // Database UUID for API calls
              _id: apiBooking._id, // Preserve _id field explicitly
              bookingRef: apiBooking.bookingReference || apiBooking.bookingRef, // Human-readable reference for display
              bookingId: apiBooking._id || apiBooking.id, // Database UUID

              // Corporate booking fields
              isCorporate,
              corporateAccount,
              corporateAccountId: corporateAccount?.id || null,
              companyName: corporateAccount?.companyName || null,
              bookingPurpose: apiBooking.bookingPurpose || null,
              costCenter: apiBooking.costCenter || null,
              bookedBy: apiBooking.bookedBy || null,

              // Customer info (use company name for corporate bookings)
              customer: {
                name: displayName,
                phone: apiBooking.customer?.phone || apiBooking.customerPhone || 'N/A',
                email: corporateAccount?.billingEmail || apiBooking.customer?.email || apiBooking.customerEmail || 'N/A'
              },
              customerName: displayName,
              customerPhone: apiBooking.customer?.phone || apiBooking.customerPhone || 'N/A',
              customerEmail: corporateAccount?.billingEmail || apiBooking.customer?.email || apiBooking.customerEmail || 'N/A',
              // For driver-rental bookings, vehicle.name is the driver's name
              ...(isDriverRental ? {
                driverName: apiBooking.vehicle?.name || 'TBD',
                driver: {
                  name: apiBooking.vehicle?.name || 'TBD',
                  driverCode: apiBooking.vehicle?.licensePlate || 'N/A',
                  rating: 0,
                  totalJobs: 0
                },
                vehicle: {
                  make: 'N/A',
                  model: 'Professional Driver',
                  year: apiBooking.vehicle?.year || apiBooking.vehicle?.yearOfManufacture || apiBooking.vehicleYear || new Date().getFullYear(),
                  type: apiBooking.vehicle?.type || 'Professional Driver',
                  plate: 'N/A' // Driver-hire bookings don't have vehicle plates
                }
              } : {
                // For vehicle rentals, parse vehicle name normally
                vehicle: {
                  make: apiBooking.vehicle?.name?.split(' ')[0] || apiBooking.vehicleName?.split(' ')[0] || 'Unknown',
                  model: apiBooking.vehicle?.name?.split(' ').slice(1).join(' ') || apiBooking.vehicleName?.split(' ').slice(1).join(' ') || 'Vehicle',
                  year: apiBooking.vehicle?.year || apiBooking.vehicle?.yearOfManufacture || apiBooking.vehicleYear || new Date().getFullYear(),
                  type: apiBooking.vehicle?.type || apiBooking.vehicleType || 'N/A',
                  plate: apiBooking.vehicle?.licensePlate || apiBooking.vehicle?.plateNumber || 'N/A'
                }
              }),
              amount: parseFloat(apiBooking.payment?.amount || apiBooking.totalCost || 0),
              totalAmount: parseFloat(apiBooking.payment?.amount || apiBooking.totalCost || 0),
              status: apiBooking.status || 'pending',
              duration: bookingDuration,
              startDate: apiBooking.booking?.startDate || apiBooking.startDate,
              endDate: apiBooking.booking?.endDate || apiBooking.endDate,
              pickupLocation: apiBooking.location?.pickup || apiBooking.pickupLocation || 'N/A',
              dropoffLocation: apiBooking.location?.dropoff || apiBooking.dropoffLocation || 'N/A',
              // Map all possible type fields (use derivedServiceType for driver-rental bookings)
              bookingType: isDriverRental ? 'driver-rental' : derivedServiceType,
              type: isDriverRental ? 'driver-rental' : derivedServiceType,
              serviceType: isDriverRental ? derivedServiceType : (apiBooking.serviceType || apiBooking.booking?.type || 'rental'),
              serviceCategory: normalizedCategory || null, // Use normalized category
              bookingEntityType: apiBooking.bookingEntityType || null,
              pickup: apiBooking.location?.pickup || apiBooking.pickupLocation || 'N/A',
              dropoff: apiBooking.location?.dropoff || apiBooking.dropoffLocation || 'N/A',
              paymentStatus: apiBooking.payment?.status || apiBooking.paymentStatus || 'pending',
              paymentMethod: apiBooking.payment?.method || apiBooking.paymentMethod || 'N/A',
              currency: apiBooking.payment?.currency || apiBooking.currency || 'NGN',
              payment: apiBooking.payment, // Preserve full payment object with security deposit data
              securityDeposit: apiBooking.payment?.securityDeposit || apiBooking.securityDeposit,
              securityDepositStatus: apiBooking.payment?.securityDepositStatus || apiBooking.securityDepositStatus,
              securityDepositHeldAt: apiBooking.payment?.securityDepositHeldAt || apiBooking.securityDepositHeldAt,
              securityDepositReleasedAt: apiBooking.payment?.securityDepositReleasedAt || apiBooking.securityDepositReleasedAt,
              createdAt: apiBooking.createdAt,
              updatedAt: apiBooking.updatedAt || apiBooking.createdAt,
              adminNotes: apiBooking.adminNotes // Preserve adminNotes for cost breakdown in receipts
            };

            return transformed;
          });

          return transformedBookings;
        } else {
          return [];
        }
      }

      const errorText = await adminResponse.text();
      log.error('API request failed', { status: adminResponse.status });
      log.error('Error response', { errorText });
      return [];
    } catch (error) {
      log.error('Error fetching bookings', error);
      return [];
    }
  },

  // Get driver rental bookings (filtered from admin endpoint)
  // NOTE: "driver-rental" = personal driver rental (just the driver, no vehicle)
  // This is DIFFERENT from "chauffeur-driven" which is a car rental WITH a chauffeur
  async getDriverRentalBookings() {
    try {
      const allBookings = await this.getAllBookings();

      // Filter for ONLY driver-rental bookings (personal driver service)
      // NOT "chauffeur-driven" (chauffeur-driven car rentals)
      const driverRentals = allBookings.filter((booking: any) => {
        const serviceCategory = booking.serviceCategory;
        const serviceType = booking.serviceType;
        const bookingType = booking.bookingType;
        const type = booking.type;
        const vehicleType = booking.vehicle?.type;

        // Only match driver-rental bookings (personal driver service)
        const isDriverRental = serviceCategory === 'driver-rental' ||
                            serviceType === 'driver-rental' ||
                            bookingType === 'driver-rental' ||
                            type === 'driver-rental' ||
                            vehicleType === 'Professional Driver';

        return isDriverRental;
      });

      log.info('Driver rental bookings filtered', { total: driverRentals.length });
      return driverRentals;
    } catch (error) {
      log.error('Error fetching driver rentals', error);
      return [];
    }
  },

  // Get car rental bookings (performance optimized - server-side filtered)
  async getCarRentals() {
    try {
      // Use admin endpoint to get ALL bookings, then filter client-side
      const allBookings = await this.getAllBookings();

      const carRentals = allBookings.filter((booking: any) => {
        // Filter for vehicle rentals only
        const isVehicleRental = booking.serviceCategory === 'vehicle-rental' ||
                               booking.serviceCategory === 'vehicle_rental' ||
                               booking.serviceCategory === 'self_drive' ||
                               booking.type === 'vehicle-rental' ||
                               booking.type === 'self_drive';

        const hasActualVehicle = booking.vehicle?.type === 'cars' ||
                                 booking.vehicle?.type === 'Luxury Sedan' ||
                                 booking.vehicle?.type === 'Luxury SUV' ||
                                 (booking.vehicle?.name &&
                                  booking.vehicle.name !== 'Professional Driver' &&
                                  booking.vehicle.name.trim() !== '');

        const isNotDriverRental = booking.serviceCategory !== 'driver-rental' &&
                               booking.serviceCategory !== 'driver_rental' &&
                               booking.type !== 'driver-rental' &&
                               booking.vehicle?.type !== 'Professional Driver';

        const isNotSpecializedService = booking.serviceCategory !== 'yacht-rental' &&
                                       booking.serviceCategory !== 'yacht_rental' &&
                                       booking.type !== 'yacht-rental' &&
                                       booking.type !== 'agent-assisted';

        return (isVehicleRental || hasActualVehicle) && isNotDriverRental && isNotSpecializedService;
      });

      if (carRentals.length > 0) {
      }
      return carRentals;
    } catch (error) {
      log.error('Error fetching car rentals', error);
      return [];
    }
  },

  // Get yacht rental bookings (performance optimized - server-side filtered)
  async getYachtRentals() {
    try {
      const url = `${getApiBaseUrl()}/api/admin/bookings/yacht-rentals`;


      // Include authorization token
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers
      });


      if (!response.ok) {
        log.error('Yacht rentals API request failed', { status: response.status, statusText: response.statusText });
        const errorText = await response.text();
        log.error('Error response body', { preview: errorText.substring(0, 500) });
        throw new Error(`Yacht rentals API error: ${response.status}`);
      }

      const result = await response.json();


      if (result.success && Array.isArray(result.data)) {

        if (!result.data.length) {
          return getMockYachts();
        }

        // Transform backend data to frontend format
        // Backend returns yacht data in 'vehicle' field with type: "Yacht"
        const transformedData = result.data.map((booking: any) => {

          return {
            ...booking,
            // Ensure yacht data is accessible in both 'yacht' and 'vehicle' fields
            yacht: booking.yacht || (booking.vehicle?.type === 'Yacht' ? {
              id: booking.vehicle.id || booking.yachtId,
              name: booking.vehicle.name,
              type: booking.vehicle.type,
              capacity: booking.vehicle.capacity || booking.vehicle.guestCapacity,
              guestCapacity: booking.vehicle.guestCapacity || booking.vehicle.capacity,
            } : undefined),
            // Keep vehicle field as-is for compatibility
            vehicle: booking.vehicle,
          };
        });

        return transformedData;
      } else {
        log.error('Unexpected yacht rentals API response structure', { result });
        log.error('Expected {success: true, data: Array}, got:', {
          success: result.success,
          dataType: typeof result.data,
          isArray: Array.isArray(result.data),
        });
        return getMockYachts();
      }
    } catch (error) {
      log.error('Error fetching yacht rentals', error);
      try {
        const allBookings = await this.getAllBookings();

        const yachts = allBookings.filter((booking: any) => {
          // Yacht rental filtering logic (for fallback compatibility)
          const isYachtRental = booking.serviceCategory === 'yacht-rental' ||
                                 booking.serviceCategory === 'yacht_rental' ||
                                 booking.type === 'yacht-rental' ||
                                 booking.type === 'yacht_rental';

          const hasYachtVehicle = booking.vehicle?.type === 'yacht' ||
                                  booking.vehicle?.type === 'boat' ||
                                  booking.vehicle?.type === 'Yacht' ||
                                  (booking.vehicle?.name &&
                                   booking.vehicle.name.trim() !== '');

          return isYachtCharter || hasYachtVehicle;
        });


        if (!yachts.length) {
          return getMockYachts();
        }
        return yachts;
      } catch (fallbackError) {
        log.error('Fallback also failed', fallbackError);
        return getMockYachts();
      }
    }
  },

  // Enable real-time booking updates via polling
  // Returns a cleanup function to stop polling
  startRealTimeUpdates(onNewBooking: (booking: any) => void, intervalMs: number = 10000): () => void {
    let knownBookingIds = new Set<string>();
    let isFirstCheck = true;

    const checkForNewBookings = async () => {
      try {
        const bookings = await this.getAllBookings();

        // First time - just store the IDs
        if (isFirstCheck) {
          bookings.forEach((b: any) => knownBookingIds.add(b.id));
          isFirstCheck = false;
          return;
        }

        // Find new bookings by comparing IDs
        const newBookings = bookings.filter((b: any) => !knownBookingIds.has(b.id));

        if (newBookings.length > 0) {
          // Add new IDs to known set
          newBookings.forEach((b: any) => {
            knownBookingIds.add(b.id);
          });

          // Notify for each new booking
          newBookings.forEach((booking: any) => {
            onNewBooking(booking);
          });
        }
      } catch (error) {
        log.error('Error checking for new bookings', error);
      }
    };

    // Start polling
    const intervalId = setInterval(checkForNewBookings, intervalMs);

    // Initial check
    checkForNewBookings();

    // Return cleanup function
    return () => {
      clearInterval(intervalId);
    };
  },

  // Get bookings by category (for dashboard filtering)
  // TERMINOLOGY CLARIFICATION:
  // - "driver-rental" = Personal driver service (driver only, uses CLIENT'S car)
  // - "chauffeur-driven" = Car rental WITH chauffeur (car + driver premium package)
  // - "self-drive" = Self-drive car rental
  async getBookingsByCategory(category: string) {
    try {
      const allBookings = await this.getAllBookings();

      // If category is 'all', return all bookings
      if (category === 'all') {
        return allBookings;
      }

      // Filter by category using normalized serviceCategory (already normalized in transformation)
      const filtered = allBookings.filter((booking: any) => {
        const serviceCategory = booking.serviceCategory; // Already normalized
        const serviceType = booking.serviceType;
        const bookingType = booking.bookingType;
        const type = booking.type;
        const vehicleType = booking.vehicle?.type;

        // Vehicle rentals (self-drive) - already normalized to "vehicle-rental"
        if (category === 'rental' || category === 'self-drive' || category === 'vehicle-rental') {
          return serviceCategory === 'vehicle-rental';
        }

        // Personal driver service (driver only, uses client's car)
        if (category === 'driver-rental' || category === 'personal-driver') {
          return serviceCategory === 'driver-rental' ||
                 serviceType === 'driver-rental' ||
                 bookingType === 'driver-rental' ||
                 type === 'driver-rental' ||
                 vehicleType === 'Professional Driver';
        }

        // Car rental WITH chauffeur (premium package: car + driver)
        if (category === 'chauffeur-driven' || category === 'rental-with-chauffeur') {
          return serviceCategory === 'vehicle-rental' && (
                 serviceType === 'chauffeur-driven' ||
                 serviceType === 'chauffeur_driven');
        }

        // Yacht rentals
        if (category === 'yacht-rental' || category === 'yacht') {
          return serviceCategory === 'yacht-rental' ||
                 serviceType === 'yacht-rental' ||
                 serviceType === 'cruise' ||
                 serviceType === 'trip';
        }

        // Generic match
        return serviceType === category || serviceCategory === category;
      });

      return filtered;
    } catch (error) {
      log.error('Error fetching bookings by category', error);
      return [];
    }
  },

  // Update a booking
  async updateBooking(bookingId: string, updateData: any) {
    try {
      const adminUrl = `${getApiBaseUrl()}/api/admin/bookings/${bookingId}`;

      const response = await fetch(adminUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error('Update failed', { status: response.status, errorText });
        throw new Error(`Failed to update booking: ${response.statusText}`);
      }

      const data = await response.json();

      // Handle different response structures
      const booking = data.data?.booking || data.booking || data.data || data;
      return booking;
    } catch (error) {
      log.error('Error updating booking', error);
      throw error;
    }
  },

  // Get a specific booking by ID
  async getBookingById(bookingId: string) {
    try {
      // Try admin endpoint first
      const adminUrl = `${getApiBaseUrl()}/api/admin/bookings/${bookingId}`;

      const response = await fetch(adminUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Fallback to searching through all bookings
        const allBookings = await this.getAllBookings();
        const booking = allBookings.find((b: any) => b.id === bookingId);
        if (!booking) {
          log.error('Booking not found');
          throw new Error('Booking not found');
        }
        return booking;
      }

      const data = await response.json();

      // Handle different response structures - try data.data first (new API structure)
      const booking = data.data || data.booking || data;


      return booking;
    } catch (error) {
      log.error('Error in getBookingById', error);
      throw error;
    }
  },

  // ========================================
  // ADMIN BOOKING MANAGEMENT ENDPOINTS
  // ========================================

  /**
   * Get detailed booking information (admin endpoint)
   * GET /api/admin/bookings/:id
   */
  async getAdminBookingById(bookingId: string) {
    try {
      const url = `${getApiBaseUrl()}/api/admin/bookings/${bookingId}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch booking: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      log.error('Error fetching booking details', error);
      throw error;
    }
  },

  /**
   * Extend a booking
   * PUT /api/admin/bookings/:id/extend
   */
  async extendBooking(bookingId: string, extensionData: {
    newEndDate: string;
    reason?: string;
    additionalCost?: number;
  }) {
    try {
      const url = `${getApiBaseUrl()}/api/admin/bookings/${bookingId}/extend`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(extensionData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to extend booking: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      log.error('Error extending booking', error);
      throw error;
    }
  },

  /**
   * Modify a booking
   * PUT /api/admin/bookings/:id/modify
   */
  async modifyBooking(bookingId: string, modifications: {
    startDate?: string;
    endDate?: string;
    pickupLocation?: string;
    dropoffLocation?: string;
    vehicleId?: string;
    notes?: string;
    reason: string;
  }) {
    try {
      const url = `${getApiBaseUrl()}/api/admin/bookings/${bookingId}/modify`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(modifications)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to modify booking: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      log.error('Error modifying booking', error);
      throw error;
    }
  },

  /**
   * Cancel a booking (admin)
   * POST /api/admin/bookings/:id/cancel
   */
  async cancelBookingAdmin(bookingId: string, cancellationData: {
    reason: string;
    refundAmount?: number;
    adminNotes?: string;
  }) {
    try {
      const url = `${getApiBaseUrl()}/api/admin/bookings/${bookingId}/cancel`;

      // Try both camelCase and snake_case for backend compatibility
      const requestBody = {
        reason: cancellationData.reason,
        refundAmount: cancellationData.refundAmount,
        refund_amount: cancellationData.refundAmount,
        adminNotes: cancellationData.adminNotes,
        admin_notes: cancellationData.adminNotes,
        cancellationReason: cancellationData.reason,
        cancellation_reason: cancellationData.reason
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error('cancelBookingAdmin failed', { status: response.status, errorText });
        throw new Error(`Failed to cancel booking: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      log.error('Error cancelling booking', error);
      throw error;
    }
  },

  /**
   * Process refund for a booking
   * POST /api/admin/bookings/:id/refund
   */
  async processRefund(bookingId: string, refundData: {
    amount: number;
    reason: string;
    refundMethod?: string;
    adminNotes?: string;
  }) {
    try {
      const url = `${getApiBaseUrl()}/api/admin/bookings/${bookingId}/refund`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(refundData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to process refund: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      log.error('Error processing refund', error);
      throw error;
    }
  },

  /**
   * Get booking modification history
   * GET /api/admin/bookings/:id/history
   */
  async getBookingHistory(bookingId: string) {
    try {
      const url = `${getApiBaseUrl()}/api/admin/bookings/${bookingId}/history`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch booking history: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      log.error('Error fetching booking history', error);
      throw error;
    }
  },

  /**
   * Assign a driver to a booking
   * POST /api/admin/bookings/:id/assign-driver
   */
  async assignDriver(bookingId: string, driverId: string, notes?: string) {
    try {
      const url = `${getApiBaseUrl()}/api/admin/bookings/${bookingId}/assign-driver`;
      const requestBody = {
        driverId,
        driver_id: driverId, // Try both camelCase and snake_case
        notes
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error('assignDriver failed', { status: response.status, errorText });
        throw new Error(`Failed to assign driver: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      log.error('Error assigning driver', error);
      throw error;
    }
  },

  /**
   * Get full service type display considering both category and type
   * This properly handles the distinction between "Car with Driver" and "Driver Hire Only"
   */
  getServiceTypeDisplayFull(serviceCategory: string | undefined, serviceType: string): { label: string; icon: string } {
    // Normalize field values (handle both snake_case and kebab-case)
    const normalizedCategory = serviceCategory?.toLowerCase().replace(/_/g, '-');
    const normalizedType = serviceType?.toLowerCase().replace(/_/g, '-');

    // Handle vehicle-rental with chauffeur = "Car with Driver"
    if (normalizedCategory === 'vehicle-rental') {
      if (normalizedType === 'chauffeur-driven') {
        return { label: 'Car with Driver', icon: '🚗👨‍💼' };
      }
      if (normalizedType === 'airport-transfer') {
        return { label: 'Airport Transfer', icon: '✈️🚗' };
      }
      if (normalizedType === 'self-drive') {
        return { label: 'Self Drive', icon: '🔑' };
      }
    }

    // Handle driver-rental = "Driver Rental Only"
    if (normalizedCategory === 'driver-rental') {
      if (normalizedType === 'hourly') {
        return { label: 'Driver Rental (Hourly)', icon: '👨‍💼⏰' };
      }
      if (normalizedType === 'daily') {
        return { label: 'Driver Rental (Daily)', icon: '👨‍💼📅' };
      }
      return { label: 'Driver Rental Only', icon: '👨‍💼' };
    }

    // Handle airport-transfer = "Airport Transfer" (always with driver + car)
    if (normalizedCategory === 'airport-transfer') {
      return { label: 'Airport Transfer', icon: '🚗👨‍💼' };
    }

    // Handle yacht-rental
    if (normalizedCategory === 'yacht-rental') {
      if (normalizedType === 'cruise') {
        return { label: 'Yacht Cruise', icon: '🛥️' };
      }
      if (normalizedType === 'trip') {
        return { label: 'Yacht Trip', icon: '⛵' };
      }
      return { label: 'Yacht Rental', icon: '🛥️' };
    }

    // Fallback - capitalize service type
    return {
      label: (serviceType || 'Unknown').replace(/-|_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      icon: '❓'
    };
  },

  /**
   * Create admin booking (for admin-created bookings via admin panel)
   * POST /api/admin/bookings
   */
  async createAdminBooking(bookingData: {
    userId: string;
    vehicleId: string;
    startDate: string;
    endDate: string;
    pickupLocation: string;
    dropoffLocation?: string;
    totalAmount: number;
    serviceCategory?: string;
    serviceType?: string;
    bookingType?: string;
    status?: string;
    paymentStatus?: string;
    customerName?: string;
    customerEmail?: string;
    customerPhone?: string;
    notes?: string;
    adminId?: string;
    corporateAccountId?: string;
  }) {
    try {
      const url = `${getApiBaseUrl()}/api/admin/bookings`;

      // Debug: Log what we're sending to backend
      console.log('🚗 [BookingService] Creating admin booking - corporateAccountId:', (bookingData as any).corporateAccountId);
      console.log('📦 [BookingService] Full request payload:', JSON.stringify(bookingData, null, 2));
      log.info('Creating admin booking', {
        userId: bookingData.userId,
        corporateAccountId: (bookingData as any).corporateAccountId,
        isCorporate: !!(bookingData as any).corporateAccountId,
        vehicleId: bookingData.vehicleId,
        serviceType: bookingData.serviceType
      });

      const response = await postWithAvailabilityOverride((body) => fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      }), bookingData as Record<string, unknown>);

      if (!response.ok) {
        const errorText = await response.text();
        log.error('createAdminBooking failed', { status: response.status, errorText });
        throw new Error(`Failed to create booking: ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      // Debug: Log what backend returned
      console.log('✅ [BookingService] Booking created - backend returned corporateAccountId:', result.data?.corporateAccountId);
      console.log('📥 [BookingService] Full backend response:', JSON.stringify(result, null, 2));
      log.info('Admin booking created', {
        bookingId: result.data?.id || result.data?._id || result.data?.bookingId,
        hasCorporateAccountId: !!result.data?.corporateAccountId,
        corporateAccountId: result.data?.corporateAccountId
      });

      // Return the booking data with _id field
      return result.data || result;
    } catch (error) {
      log.error('Error creating admin booking', error);
      throw error;
    }
  },

  // Get bookings for a specific corporate account
  async getCorporateAccountBookings(corporateAccountId: string, options?: { page?: number; limit?: number; status?: string }) {
    try {
      log.info('Fetching corporate account bookings', { corporateAccountId, options });

      // Build query parameters
      const params = new URLSearchParams();
      params.append('corporateAccountId', corporateAccountId);
      if (options?.page) params.append('page', options.page.toString());
      params.append('limit', (options?.limit || 100).toString());
      if (options?.status) params.append('status', options.status);

      const queryString = params.toString();
      const url = `${getApiBaseUrl()}/api/admin/bookings${queryString ? `?${queryString}` : ''}`;

      log.debug('Making API request for corporate bookings', { url, queryString });

      // Prepare headers with authentication
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (response.ok) {
        const responseText = await response.text();

        let responseData;
        try {
          responseData = JSON.parse(responseText);
        } catch (parseError) {
          log.error('JSON parse error for corporate bookings', parseError);
          return [];
        }

        // Handle multiple possible API response structures
        let bookingsData = [];

        if (responseData.success && responseData.data) {
          bookingsData = responseData.data;
        } else if (Array.isArray(responseData.data)) {
          bookingsData = responseData.data;
        } else if (Array.isArray(responseData)) {
          bookingsData = responseData;
        } else if (responseData.bookings && Array.isArray(responseData.bookings)) {
          bookingsData = responseData.bookings;
        } else {
          log.error('Unexpected response structure for corporate bookings', { keys: Object.keys(responseData) });
          return [];
        }

        log.info('Corporate bookings fetched successfully', { count: bookingsData.length });

        // Debug: Log first few bookings to see structure
        if (bookingsData.length > 0) {
          console.log('📦 First booking structure:', bookingsData[0]);
          console.log('📦 First booking corporateAccountId:', bookingsData[0]?.corporateAccountId);
          console.log('📦 First booking corporateAccount:', bookingsData[0]?.corporateAccount);
          console.log('📦 All top-level keys in first booking:', Object.keys(bookingsData[0]));
          console.log('📦 Looking for corporate in any field:', Object.entries(bookingsData[0])
            .filter(([key, value]) =>
              key.toLowerCase().includes('corporate') ||
              (typeof value === 'string' && value.includes(corporateAccountId))
            )
          );
        }

        // Filter client-side by corporateAccountId (using correct nested structure)
        const filteredBookings = bookingsData.filter((apiBooking: any) => {
          // Backend returns corporateAccount.id, not corporateAccountId at top level
          const matches = apiBooking.corporateAccount?.id === corporateAccountId ||
                         apiBooking.corporateAccount?._id === corporateAccountId ||
                         apiBooking.corporateAccountId === corporateAccountId; // Fallback for old data

          if (matches) {
            console.log('✅ Matched booking:', apiBooking.bookingReference || apiBooking._id);
          }
          return matches;
        });

        console.log('📊 Filtered corporate bookings', {
          total: bookingsData.length,
          filtered: filteredBookings.length,
          corporateAccountId,
          sampleBookingIds: bookingsData.slice(0, 3).map((b: any) => ({
            ref: b.bookingReference,
            corpId: b.corporateAccountId,
            corpAccountId: b.corporateAccount?.id
          }))
        });

        // Transform API data (reuse existing transformation logic)
        const transformedBookings = filteredBookings.map((apiBooking: any) => {
          // Normalize serviceCategory
          let normalizedCategory = apiBooking.serviceCategory;
          if (apiBooking.serviceCategory === 'self_drive' || apiBooking.serviceCategory === 'vehicle') {
            normalizedCategory = 'vehicle-rental';
          }

          // Extract corporate data from correct nested structure
          const isCorporate = apiBooking.isCorporate || false;
          const corporateAccount = apiBooking.corporateAccount || null;
          const companyName = corporateAccount?.companyName || null;

          return {
            id: apiBooking._id || apiBooking.id,
            _id: apiBooking._id,
            bookingRef: apiBooking.bookingReference || apiBooking.bookingRef,
            bookingId: apiBooking._id || apiBooking.id,

            // Corporate fields (from nested structure)
            isCorporate,
            corporateAccount,
            corporateAccountId: corporateAccount?.id || null,
            companyName,
            bookingPurpose: apiBooking.bookingPurpose || null,
            costCenter: apiBooking.costCenter || null,
            bookedBy: apiBooking.bookedBy || null,

            // Customer info (use company name for corporate bookings)
            customer: {
              name: companyName || apiBooking.customer?.name || apiBooking.customerName || 'Unknown Customer',
              phone: apiBooking.customer?.phone || apiBooking.customerPhone || 'N/A',
              email: corporateAccount?.billingEmail || apiBooking.customer?.email || apiBooking.customerEmail || 'N/A'
            },
            customerName: companyName || apiBooking.customer?.name || apiBooking.customerName || 'Unknown Customer',
            customerPhone: apiBooking.customer?.phone || apiBooking.customerPhone || 'N/A',
            customerEmail: corporateAccount?.billingEmail || apiBooking.customer?.email || apiBooking.customerEmail || 'N/A',

            // Preserve for backward compat
            isCorporateBooking: isCorporate,

            vehicle: {
              name: apiBooking.vehicle?.name || 'N/A',
              type: apiBooking.vehicle?.type || 'N/A',
              licensePlate: apiBooking.vehicle?.licensePlate || 'N/A'
            },
            serviceCategory: normalizedCategory,
            serviceType: apiBooking.serviceType || 'rental',
            status: apiBooking.status || 'pending',
            totalAmount: apiBooking.totalAmount || apiBooking.pricing?.total || 0,
            startDate: apiBooking.startDate || apiBooking.booking?.startDate,
            endDate: apiBooking.endDate || apiBooking.booking?.endDate,
            pickupLocation: apiBooking.pickupLocation || apiBooking.booking?.pickupLocation,
            dropoffLocation: apiBooking.dropoffLocation || apiBooking.booking?.dropoffLocation,
            createdAt: apiBooking.createdAt,
            updatedAt: apiBooking.updatedAt
          };
        });

        return transformedBookings;
      }

      const errorText = await response.text();
      log.error('API request failed for corporate bookings', { status: response.status, errorText });
      return [];
    } catch (error) {
      log.error('Error fetching corporate account bookings', error);
      return [];
    }
  }
};

// Legacy interfaces for backward compatibility
interface LegacyBookingFilters {
  status?: string[];
  userId?: string;
  vehicleId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface LegacyCreateBookingData {
  vehicleId: string;
  type: BookingType;
  startDate: string;
  endDate: string;
  pickupLocation: string;
  dropoffLocation?: string;
  notes?: string;
}

interface BookingStats {
  totalBookings: number;
  activeBookings: number;
  completedBookings: number;
  totalRevenue: number;
  averageBookingValue: number;
}

interface PaginatedBookingResponse {
  data: Booking[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AdminCreateBookingData {
  userId: string; // REQUIRED: Customer's user ID
  vehicleId?: string; // REQUIRED for vehicle rentals
  driverId?: string; // REQUIRED for driver hires
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  pickupLocation: string;
  dropoffLocation?: string;
  totalAmount: number;
  paymentMethod?: string;
  paymentStatus?: string;
  status?: string;
  serviceType?: string;
  bookingType?: string;
  customerName?: string; // For display
  vehicleName?: string; // For display
  driverName?: string; // For display
  notes?: string;
}

/**
 * Enhanced booking service with comprehensive booking management capabilities
 * Supports new service categories, approval workflows, and complex pricing
 */
class BookingService {
  private baseUrl = '/api/v1/bookings';

  /**
   * Legacy getBookings method for backward compatibility
   */
  async getBookings(filters: LegacyBookingFilters = {}, page = 1, limit = 20): Promise<PaginatedBookingResponse> {
    // Simplified implementation - call simplBookingService instead
    const data = await simplBookingService.getAllBookings({
      page,
      limit,
      status: filters.status?.join(','),
      search: filters.search
    });

    return {
      bookings: data,
      pagination: {
        page,
        limit,
        total: data.length,
        totalPages: Math.ceil(data.length / limit)
      }
    };
  }

  /**
   * Legacy createBooking method
   */
  async createBooking(bookingData: LegacyCreateBookingData): Promise<LegacyBooking> {
    // Convert legacy booking data to enhanced format
    const enhancedBookingData: BookingForm = {
      serviceCategory: ServiceCategory.VEHICLE_RENTAL,
      serviceType: bookingData.type === 'rental' ? VehicleServiceType.SELF_DRIVE : VehicleServiceType.CHAUFFEUR_DRIVEN,
      durationType: BookingDurationType.DAILY,
      customerId: 'current-user', // Should be populated from auth context
      vehicleId: bookingData.vehicleId,
      startDate: bookingData.startDate,
      endDate: bookingData.endDate,
      pickupLocation: bookingData.pickupLocation,
      dropoffLocation: bookingData.dropoffLocation,
      notes: bookingData.notes,
    };

    const response = await this.createEnhancedBooking(enhancedBookingData);
    
    // Convert enhanced response back to legacy format
    return {
      id: response.bookingId,
      bookingRef: generateBookingReference(response.bookingId, bookingData.type),
      ...bookingData,
      status: 'pending' as const,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  /**
   * Create admin booking (for admin panel)
   */
  async createAdminBooking(bookingData: AdminCreateBookingData): Promise<any> {
    // This would typically call the admin booking creation endpoint
    const response = await apiClient.post('/api/admin/bookings', bookingData);
    return response.data;
  }

  // Standard booking management methods
  async getBookingById(id: string): Promise<Booking> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data;
  }

  async updateBooking(id: string, bookingData: Partial<Booking>): Promise<Booking> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, bookingData);
    return response.data.data;
  }

  async cancelBooking(id: string, reason?: string): Promise<void> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/cancel`, { reason });
    return response.data.data;
  }

  async getBookingStats(): Promise<BookingStats> {
    const response = await apiClient.get(`${this.baseUrl}/stats`);
    return response.data.data;
  }

  async confirmBooking(id: string): Promise<void> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/confirm`);
    return response.data.data;
  }

  // Service type and category utilities
  getServiceCategoryDisplay(category: ServiceCategory) {
    const displays = {
      [ServiceCategory.VEHICLE_RENTAL]: { label: 'Vehicle Rental', icon: '🚗' },
      [ServiceCategory.DRIVER_HIRE]: { label: 'Driver Hire', icon: '👨‍💼' },
      [ServiceCategory.YACHT_CHARTER]: { label: 'Yacht Charter', icon: '🛥️' },
      [ServiceCategory.PACKAGE_SERVICE]: { label: 'Package Service', icon: '📦' },
    };
    return displays[category] || { label: category, icon: '❓' };
  }

  getServiceTypeDisplay(serviceType: VehicleServiceType | DriverServiceType) {
    const displays = {
      [VehicleServiceType.SELF_DRIVE]: { label: 'Self Drive', icon: '🔑' },
      [VehicleServiceType.CHAUFFEUR_DRIVEN]: { label: 'Chauffeur Driven', icon: '👨‍💼' },
      [DriverServiceType.PERSONAL_DRIVER]: { label: 'Personal Driver', icon: '🚗' },
  // Removed invalid enum members
    };
    return displays[serviceType as keyof typeof displays] || { label: serviceType, icon: '❓' };
  }

  /**
   * Get full service type display considering both category and type
   * This properly handles the distinction between "Car with Driver" and "Driver Hire Only"
   */
  getServiceTypeDisplayFull(serviceCategory: string | undefined, serviceType: string): { label: string; icon: string } {
    // Normalize field values (handle both snake_case and kebab-case)
    const normalizedCategory = serviceCategory?.toLowerCase().replace(/_/g, '-');
    const normalizedType = serviceType?.toLowerCase().replace(/_/g, '-');

    // Handle vehicle-rental with chauffeur = "Car with Driver"
    if (normalizedCategory === 'vehicle-rental') {
      if (normalizedType === 'chauffeur-driven') {
        return { label: 'Car with Driver', icon: '🚗👨‍💼' };
      }
      if (normalizedType === 'airport-transfer') {
        return { label: 'Airport Transfer', icon: '✈️🚗' };
      }
      if (normalizedType === 'self-drive') {
        return { label: 'Self Drive', icon: '🔑' };
      }
    }

    // Handle driver-rental = "Driver Rental Only"
    if (normalizedCategory === 'driver-rental') {
      if (normalizedType === 'hourly') {
        return { label: 'Driver Rental (Hourly)', icon: '👨‍💼⏰' };
      }
      if (normalizedType === 'daily') {
        return { label: 'Driver Rental (Daily)', icon: '👨‍💼📅' };
      }
      return { label: 'Driver Rental Only', icon: '👨‍💼' };
    }

    // Handle airport-transfer = "Airport Transfer" (always with driver + car)
    if (normalizedCategory === 'airport-transfer') {
      return { label: 'Airport Transfer', icon: '🚗👨‍💼' };
    }

    // Handle yacht-rental
    if (normalizedCategory === 'yacht-rental') {
      if (normalizedType === 'cruise') {
        return { label: 'Yacht Cruise', icon: '🛥️' };
      }
      if (normalizedType === 'trip') {
        return { label: 'Yacht Trip', icon: '⛵' };
      }
      return { label: 'Yacht Rental', icon: '🛥️' };
    }

    // Fallback to existing logic
    return this.getServiceTypeDisplay(serviceType as any);
  }

  getApprovalTypeDisplay(approvalType: BookingApprovalType) {
    const displays = {
      [BookingApprovalType.INSTANT]: { label: 'Instant Booking', color: 'success' },
      [BookingApprovalType.REQUEST_TO_BOOK]: { label: 'Request to Book', color: 'warning' },
      [BookingApprovalType.ADMIN_APPROVAL]: { label: 'Admin Approval', color: 'info' },
      [BookingApprovalType.DUAL_APPROVAL]: { label: 'Dual Approval', color: 'secondary' },
    };
    return displays[approvalType] || { label: approvalType, color: 'default' };
  }

  canApproveBooking(booking: Booking, userRole: string): boolean {
    // Logic to determine if user can approve this booking
    // This would be based on the booking's approval workflow and user permissions
    return ['admin', 'manager', 'supervisor'].includes(userRole.toLowerCase());
  }

  calculateEnhancedPricing(
    baseRate: number,
    duration: number,
    serviceType: VehicleServiceType,
    additionalServices?: string[]
  ): PricingBreakdown {
    let totalAmount = baseRate * duration;
    let chauffeurSurcharge = 0;
    let serviceFee = totalAmount * 0.05; // 5% service fee
    let securityDeposit = 0;

    // Add chauffeur surcharge
    if (serviceType === VehicleServiceType.CHAUFFEUR_DRIVEN) {
      chauffeurSurcharge = totalAmount * 0.3; // 30% surcharge
      totalAmount += chauffeurSurcharge;
    }

    // Security deposit for certain service types
    if (serviceType === VehicleServiceType.SELF_DRIVE) {
      securityDeposit = Math.min(totalAmount * 0.2, 500000); // 20% or max ₦500k
    }

    // Calculate tax (7.5% VAT in Nigeria)
    const taxRate = 0.075;
    const taxAmount = (totalAmount + serviceFee) * taxRate;
    
    const finalAmount = totalAmount + serviceFee + taxAmount;

    return {
      baseRate: baseRate * duration,
      chauffeurSurcharge,
      serviceFee,
      taxAmount,
      securityDeposit,
      totalAmount: finalAmount,
      currency: 'NGN',
      breakdown: {
        baseService: baseRate * duration,
        additionalServices: chauffeurSurcharge,
        fees: serviceFee,
        taxes: taxAmount,
      }
    };
  }

  // NEW: Enhanced service type normalization and helper methods
  
  /**
   * Normalize service category to backend enum format
   */
  private normalizeServiceCategory(category: ServiceCategory | string): string {
    const mapping: Record<string, string> = {
      [ServiceCategory.VEHICLE_RENTAL]: 'vehicle_rental',
      [ServiceCategory.DRIVER_RENTAL]: 'driver_rental',
      [ServiceCategory.PACKAGE_SERVICE]: 'package_service',
      [ServiceCategory.YACHT_RENTAL]: 'yacht_rental',
      // Legacy mappings for backward compatibility
      'driver-hire': 'driver_rental',
      'yacht-charter': 'yacht_rental',
    };

    return mapping[category] || 'vehicle_rental';
  }

  /**
   * Normalize service type to backend enum format
   */
  private normalizeServiceType(serviceType: VehicleServiceType | DriverServiceType | string): string {
    const mapping: Record<string, string> = {
      [VehicleServiceType.SELF_DRIVE]: 'self_drive',
      [VehicleServiceType.CHAUFFEUR_DRIVEN]: 'chauffeur_driven',
      [VehicleServiceType.AIRPORT_TRANSFER]: 'airport_transfer',
      [DriverServiceType.HOURLY]: 'hourly',
      [DriverServiceType.DAILY]: 'daily',
      // Legacy mappings for backward compatibility
      'with-driver': 'chauffeur_driven',
      'driver-only': 'hourly',
      'personal-driver': 'hourly',
      'rental': 'self_drive'
    };

    return mapping[serviceType] || 'self_drive';
  }

  /**
   * Determine booking channel based on current context
   */
  private determineBookingChannel(): string {
    // Check if running in admin context
    if (window.location.pathname.includes('/admin')) {
      return 'admin_panel';
    }
    
    // Check if mobile app context (would need to be set by mobile app)
    if ((window as any).ReactNativeWebView || navigator.userAgent.includes('PolarRideMobile')) {
      return 'mobile_app';
    }
    
    // Default to web platform
    return 'web_platform';
  }

  /**
   * Determine approval type based on booking data
   */
  private determineApprovalType(bookingData: BookingForm): string {
    // High-value bookings require approval
    if (this.isHighValueBooking(bookingData)) {
      return 'admin_approval';
    }
    
    // Enterprise bookings require approval  
    if (this.determineEnterpriseFlag(bookingData)) {
      return 'request_to_book';
    }
    
    // Chauffeur services typically require approval
    if (bookingData.serviceType === VehicleServiceType.CHAUFFEUR_DRIVEN ||
        bookingData.serviceType === DriverServiceType.CHAUFFEUR_SERVICE) {
      return 'request_to_book';
    }
    
    // Default to instant booking
    return 'instant';
  }

  /**
   * Determine duration type based on date range
   */
  private determineDurationType(startDate: string, endDate: string): string {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    const diffDays = diffHours / 24;
    
    if (diffHours <= 24) return 'hourly';
    if (diffDays <= 7) return 'daily';
    if (diffDays <= 30) return 'weekly';
    if (diffDays <= 90) return 'monthly';
    
    return 'custom';
  }

  /**
   * Get device type for metadata
   */
  private getDeviceType(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (userAgent.includes('mobile') || userAgent.includes('android') || userAgent.includes('iphone')) {
      return 'mobile';
    }
    
    if (userAgent.includes('tablet') || userAgent.includes('ipad')) {
      return 'tablet';
    }
    
    return 'desktop';
  }

  /**
   * Determine if booking qualifies as enterprise
   */
  private determineEnterpriseFlag(bookingData: BookingForm): boolean {
    // Check if customer email suggests enterprise
    const email = bookingData.customerId; // This would need to be enhanced
    if (email && (email.includes('@company.com') || email.includes('@corp.') || email.includes('@enterprise.'))) {
      return true;
    }
    
    // Check for multiple vehicle bookings or high frequency
    // This would need to query user booking history
    
    return false;
  }

  /**
   * Check if booking is high value
   */
  private isHighValueBooking(bookingData: BookingForm): boolean {
    // This would need actual pricing calculation
    // For now, assume chauffeur services are high value
    return bookingData.serviceType === VehicleServiceType.CHAUFFEUR_DRIVEN ||
           bookingData.serviceType === DriverServiceType.CHAUFFEUR_SERVICE;
  }

  /**
   * Generate system notes without abusing the notes field
   */
  private generateSystemNotes(bookingData: BookingForm): string {
    const serviceTypeDisplay = this.getServiceTypeDisplay(bookingData.serviceType).label;
    const serviceCategoryDisplay = this.getServiceCategoryDisplay(bookingData.serviceCategory).label;
    const channel = this.determineBookingChannel().replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    
    return `${channel} - ${serviceCategoryDisplay} (${serviceTypeDisplay}) - Created via Enhanced Booking System`;
  }

  /**
   * Map enhanced booking data to legacy format for fallback
   */
  private mapToLegacyFormat(enhancedData: any): any {
    return {
      vehicleId: enhancedData.vehicleId,
      startDate: enhancedData.startDate,
      endDate: enhancedData.endDate,
      pickupLocation: enhancedData.pickupLocation,
      dropoffLocation: enhancedData.dropoffLocation,
      // Map service types back to legacy format
      serviceType: this.mapServiceTypeToLegacy(enhancedData.serviceType),
      bookingType: enhancedData.approvalType === 'instant' ? 'instant' : 'request',
      // Include original notes + system tracking
      notes: `${enhancedData.notes}\n\n[System] Service: ${enhancedData.serviceType}, Channel: ${enhancedData.bookingChannel}`,
      paymentMethod: enhancedData.paymentMethod,
      additionalServices: enhancedData.additionalServices,
      estimatedMileage: enhancedData.estimatedMileage
    };
  }

  /**
   * Map enhanced service type back to legacy format
   */
  private mapServiceTypeToLegacy(enhancedType: string): string {
    const mapping: Record<string, string> = {
      'self_drive': 'self-drive',
      'with_driver': 'with-driver', 
      'chauffeur_driven': 'chauffeur-driven',
      'personal_driver': 'with-driver',
      'delivery_driver': 'with-driver'
    };
    
    return mapping[enhancedType] || 'self-drive';
  }
}

// Export the service instance
export const bookingService = new BookingService();

// Export both for compatibility
export { bookingService as default };